import { Slider } from "../common/services/sliderService";
const sliderConfigs = {
    splide__1: {
        perPage: 1,
        gap: '16px',
        breakpoints: {
            768: { perPage: 1 },
        },
    },
    splide__2: {
        perPage: 2,
        pagination: true,
        breakpoints: {
            768: { perPage: 1 },
        },
    },
    splide__3: {
        perPage: 3,
        pagination: true,
        breakpoints: {
            768: { perPage: 1 },
            600: { perPage: 1 },
        },
    },
    splide__4: {
        perPage: 4,
    },
    splide__5: {
        perPage: 5,
        arrows: false,
        breakpoints: {
            768: { type: 'loop', perPage: 3, arrows: true, autoplay: true, speed: 3000 },
            600: { type: 'loop', perPage: 2, arrows: true, autoplay: true, speed: 3000 },
        },
    },
    splide__6: {
        perPage: 6,
        breakpoints: {
            768: { perPage: 4 },
            600: { perPage: 2 },
        },
    },
    splide__8: {
        perPage: 8,
        breakpoints: {
            1280: { perPage: 6 },
            768: { perPage: 4 },
            600: { perPage: 2 },
        },
    },
};
/**
 * Determina si el slider corresponde a una card de proyecto,
 * lo cual afecta principalmente estilos (flechas, UX).
 *
 * @param element Elemento raíz del slider
 * @returns true si es un slider de tipo card, false en caso contrario
 */
function isCardSlider(element) {
    return element.classList.contains('card-project__slider') || element.classList.contains('map__slider');
}
/**
 * Extrae información del DOM necesaria para decidir el comportamiento del slider
 * sin acoplar lógica de configuración ni forzar cálculos innecesarios de layout.
 *
 * Esta función:
 * - Cuenta el número de slides disponibles
 * - Detecta si el slider usa paginación
 * - Detecta si tiene autoplay habilitado vía clases
 *
 *
 * @param element Elemento raíz del slider (contenedor Splide)
 * @returns Objeto con datos derivados del DOM necesarios para configurar el slider
 */
function precomputeSliderData(element) {
    var _a;
    const list = element.querySelector('.splide__list');
    return {
        totalItems: (_a = list === null || list === void 0 ? void 0 : list.children.length) !== null && _a !== void 0 ? _a : 0,
        isPagination: element.classList.contains('splide-pagination'),
        autoPlay: element.classList.contains('autoplay'),
        autoScroll: element.classList.contains('autoscroll'),
    };
}
/**
 * Construye la configuración final del slider combinando:
 * - la configuración base definida
 * - los datos derivados del DOM
 * - el contexto actual del viewport
 *
 * Esta función:
 * - Resuelve el `perPage` efectivo según breakpoints
 * - Determina si hay overflow de slides
 * - Activa o desactiva flechas/paginación dinámicamente
 * - Ajusta autoplay y tipo de slider si aplica
 *
 *
 * @param baseConfig Configuración base del slider (parcial)
 * @param data Datos precomputados del DOM (slides, flags, etc.)
 * @param screenWidth Ancho actual del viewport
 * @returns Configuración final lista para ser pasada a Splide
 */
function buildConfig(baseConfig, data, screenWidth) {
    var _a;
    let perPage = (_a = baseConfig.perPage) !== null && _a !== void 0 ? _a : 1;
    if (baseConfig.breakpoints) {
        const sorted = Object.keys(baseConfig.breakpoints)
            .map(Number)
            .sort((a, b) => a - b);
        for (const bp of sorted) {
            const cfg = baseConfig.breakpoints[bp];
            if (screenWidth <= bp && (cfg === null || cfg === void 0 ? void 0 : cfg.perPage)) {
                perPage = cfg.perPage;
                break;
            }
        }
    }
    const hasOverflow = data.totalItems > perPage;
    return {
        ...baseConfig,
        arrows: hasOverflow && !data.isPagination && !data.autoScroll,
        pagination: hasOverflow && data.isPagination && !data.autoScroll,
        autoplay: data.autoPlay && hasOverflow,
        rewind: data.autoPlay && hasOverflow,
        ...(data.autoScroll && {
            type: 'loop',
            autoScroll: {
                speed: 1,
            },
        }),
    };
}
/**
 * Inicializa un slider estático.
 *
 * Un slider estático:
 * - Se crea una sola vez
 * - No cambia su contenido después
 * - Puede refrescarse, pero NO se destruye
 *
 * Este método:
 * - Resuelve la configuración final
 * - Usa createOnce internamente
 *
 * @param element Elemento raíz del slider
 * @param baseConfig Configuración parcial del slider
 * @param options.lazy Si es true, inicializa el slider al entrar en viewport
 */
export function createStaticSlider(element, baseConfig = {}, options = {}) {
    if (!element || element.dataset.sliderInit === 'true')
        return;
    const screenWidth = window.innerWidth;
    const data = precomputeSliderData(element);
    const finalConfig = buildConfig(baseConfig, data, screenWidth);
    const mount = () => {
        Slider.createOnce(`#${element.id}`, finalConfig, isCardSlider(element));
        element.dataset.sliderInit = 'true';
    };
    if (options.lazy) {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                mount();
                observer.disconnect();
            }
        }, { rootMargin: '200px' });
        observer.observe(element);
    }
    else {
        requestAnimationFrame(mount);
    }
}
/**
 * Inicializa o recrea un slider dinámico.
 *
 * Un slider dinámico:
 * - Cambia su DOM interno (slides)
 * - Requiere destroy + recreate
 * - Puede cambiar configuración (speed, rewind, UX)
 *
 * Flujo:
 * 1. Destruye cualquier instancia previa
 * 2. Espera al siguiente frame real de render
 * 3. Recrea el slider con la nueva configuración
 *
 * @param element Elemento raíz del slider
 * @param config Configuración completa del slider dinámico
 */
export function createDynamicSlider(element, config) {
    var _a;
    destroySlider(element.id);
    requestAnimationFrame(() => {
        Slider.recreate(`#${element.id}`, config, true);
    });
    const instance = (_a = Slider.splides) === null || _a === void 0 ? void 0 : _a['#slider-compare'];
}
/**
 * Destruye explícitamente un slider por su ID.
 *
 * @param id ID del elemento slider (sin #)
 */
export function destroySlider(id) {
    Slider.destroy(id);
}
/**
 * Recrea explícitamente un slider por su ID.
 *
 * @param id ID del slider (sin #)
 * @param config Nueva configuración del slider
 * @param isCard Indica si es un slider tipo card
 */
export function recreateSlider(id, config, isCard = true) {
    Slider.recreate(id, config, isCard);
}
/**
 * Inicializa todos los sliders estáticos definidos en sliderConfigs.
 *
 * - Recorre las clases configuradas
 * - Inicializa cada slider encontrado
 * - Excluye explícitamente sliders dinámicos (ej: slider-compare)
 *
 * @param context Nodo raíz desde donde buscar sliders (por defecto document)
 */
export function loadSliders(context = document) {
    Object.entries(sliderConfigs).forEach(([className, baseConfig]) => {
        const elements = Array.from(context.querySelectorAll(`.${className}`)).filter((el) => el.id && el.id !== 'slider-compare');
        elements.forEach((element) => {
            const isCardSlider = element.classList.contains('card-project__slider');
            const config = { ...baseConfig };
            if (isCardSlider) {
                config.drag = false;
                config.pagination = false;
            }
            createStaticSlider(element, {
                ...config,
            }, { lazy: true });
        });
    });
}
/**
 * Ejecuta un callback cuando el DOM ya ha sido renderizado
 * en el siguiente frame.
 *
 * Este helper reemplaza setTimeout arbitrarios y ayuda a:
 * - evitar race conditions
 * - inicializar sliders cuando el HTML ya existe
 *
 * @param callback Función a ejecutar
 * @param retries Número máximo de intentos (por defecto 10)
 */
export function initWhenReady(callback, retries = 10) {
    let attempts = 0;
    const run = () => {
        if (attempts >= retries)
            return;
        attempts++;
        requestAnimationFrame(() => {
            callback();
        });
    };
    run();
}
